import { memo, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from 'src/modules/common/types/Breadcrumbs';
import { Breadcrumbs as SensorsBreadcrumbs } from 'src/sensors/Breadcrumbs.ts';
import { Breadcrumbs as ActuatorsBreadcrumbs } from 'src/actuators/Breadcrumbs.ts';
import { DropdownField } from 'src/modules/common/components/DropdownField/DropdownField';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import clsx from 'clsx';
import { BREADCRUMB_STATUS } from 'src/common/types/BreadcrumbStatus';
import { MarkInProgressIcon } from 'src/modules/common/icons/MarkInProgressIcon.tsx';
import { MarkEmptyIcon } from 'src/modules/common/icons/MarkEmptyIcon.tsx';
import { MarkDoneIcon } from 'src/modules/common/icons/MarkDoneIcon.tsx';
import { CrossIcon } from 'src/modules/common/icons/CrossIcon.tsx';
import { MESSAGES } from 'src/modules/common/constants';
import { BreadcrumbProps } from 'src/common/Breadcrumb/Breadcrumb.tsx';

type Props = {
    breadcrumbs: Breadcrumbs | SensorsBreadcrumbs | ActuatorsBreadcrumbs;
};

export const BreadcrumbMobileDropdown = memo(({ breadcrumbs }: Props) => {
    const navigate = useNavigate();
    const currentBreadcrumbIndex = breadcrumbs.findIndex(
        (breadcrumb) => breadcrumb.breadcrumbStatus === BREADCRUMB_STATUS.PROGRESS,
    );
    const currentBreadcrumb = breadcrumbs[currentBreadcrumbIndex] ?? breadcrumbs[0];
    const currentStepNumber = currentBreadcrumbIndex === -1 ? 1 : currentBreadcrumbIndex + 1;
    const totalSteps = breadcrumbs.length;

    const getBreadcrumb = (title: string) => breadcrumbs.find((b) => b.title === title);

    const onBreadcrumbSelection = (title: string) => {
        const selectedBreadcrumb = getBreadcrumb(title);

        if (!selectedBreadcrumb || selectedBreadcrumb.disabled) {
            return;
        }

        navigate(selectedBreadcrumb.to, { state: selectedBreadcrumb.linkState });
    };

    const getStatusIcon = (status: BREADCRUMB_STATUS) => {
        if (status === BREADCRUMB_STATUS.PROGRESS) {
            return <MarkInProgressIcon/>;
        }

        if (status === BREADCRUMB_STATUS.EMPTY) {
            return <MarkEmptyIcon/>;
        }

        return <MarkDoneIcon/>;
    };

    const handleActionClick = (breadcrumb: BreadcrumbProps) => (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        breadcrumb.onActionClick?.(e);
    };

    return (
        <div className="vss-breadcrumb-mobile-dropdown">
            <DropdownField
                selectedValue={currentBreadcrumb.title}
                options={breadcrumbs.map((breadcrumb) => breadcrumb.title)}
                onChange={onBreadcrumbSelection}
                hideCheckIcon={true}
                shaded={false}
                isOptionDisabled={(title) => getBreadcrumb(title)?.disabled ?? false}
                renderSelectedValue={(title) => {
                    const breadcrumb = getBreadcrumb(title);

                    if (!breadcrumb) {
                        return null;
                    }

                    return (
                        <>
                            <div className={clsx({
                                'vss-breadcrumb__status-mark': true,
                                'vss-breadcrumb__status-mark--empty':
                                    breadcrumb.breadcrumbStatus === BREADCRUMB_STATUS.EMPTY,
                            })}
                            >
                                {getStatusIcon(breadcrumb.breadcrumbStatus)}
                            </div>

                            <I18n messageId={title}/>
                        </>
                    );
                }}
                renderOption={(title) => {
                    const breadcrumb = getBreadcrumb(title);

                    if (!breadcrumb) {
                        return null;
                    }

                    const isCurrent = breadcrumb.breadcrumbStatus === BREADCRUMB_STATUS.PROGRESS;

                    return (
                        <div className={clsx({
                            'vss-breadcrumb': true,
                            'vss-breadcrumb--filled': breadcrumb.breadcrumbStatus === BREADCRUMB_STATUS.DONE,
                            'vss-breadcrumb--selected': isCurrent,
                            'vss-breadcrumb--disabled': breadcrumb.disabled,
                        })}
                        >
                            <div className={clsx({
                                'vss-breadcrumb__status-mark': true,
                                'vss-breadcrumb__status-mark--empty':
                                    breadcrumb.breadcrumbStatus === BREADCRUMB_STATUS.EMPTY,
                            })}
                            >
                                {getStatusIcon(breadcrumb.breadcrumbStatus)}
                            </div>

                            <div className="vss-breadcrumb__contents">
                                <p className="vss-breadcrumb__title">
                                    <I18n messageId={breadcrumb.title}/>
                                </p>

                                <div className="vss-breadcrumb__value">
                                    {breadcrumb.value || '-'}
                                </div>
                            </div>

                            {isCurrent && breadcrumb.onActionClick && (
                                <div
                                    data-testid="breadcrumb-action"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={() => null}
                                    className="vss-breadcrumb__action"
                                    onClick={handleActionClick(breadcrumb)}
                                >
                                    <CrossIcon/>
                                </div>
                            )}
                        </div>
                    );
                }}
            />

            <span className="vss-breadcrumb-mobile-dropdown__step-count">
                <I18n
                    messageId={MESSAGES.breadcrumbStepCounter}
                    params={{ current: currentStepNumber, total: totalSteps }}
                />
            </span>
        </div>
    );
});
